import { computed, ref, watch, watchEffect, onMounted } from 'vue'
import { Render } from 'src/utils/helper/render'

import { groupBy, head, isEmpty, isEqual, map } from 'lodash'
import i18n from 'src/assets/i18n/i18n'

import FamilyItem from '../../family-item/FamilyItem.vue'
import CloseIcon from '../../../../../components/icons/close/Close.vue'
import BackIcon from '../../../components/icons/back-icon/index.vue'
import ArrowBack from '../../../components/icons/arrow-back/ArrowBack.vue'
import MFPIcon from 'src/pages/bundle-submission/components/icons/mfp-icon/index.vue'
import DotIcon from 'src/pages/bundle-submission/components/icons/dot-icon/DotIcon.vue'
import FontPreviewer from 'src/pages/bundle-submission/components/font-previewer/FontPreviewer.vue'
import AnimationProcessing from 'components/animations/processing'

import { Carousel, Slide } from 'vue3-carousel'
import Misc from 'src/utils/helper/misc'

export default {
  name: 'PreviewDraft',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    bundlePreviewData: {
      type: Object,
      required: true
    },
    savingDraft: {
      type: Boolean,
      required: true
    }
  },
  components: {
    CloseIcon,
    BackIcon,
    ArrowBack,
    FamilyItem,
    MFPIcon,
    FontPreviewer,
    Carousel,
    Slide,
    DotIcon,
    AnimationProcessing
  },
  setup(props, { emit }) {
    const PREVIEW_DRAFT_CONSTANTS = {
      max_truncate_string_length: 50,
      family_name_truncate_length: 23,
    }

    const closeHandler = (status) => {
      emit('update:modelValue', status)
    }

    /**
     * Writable computed property to handle v-model
     */
    const computedModelValue = computed({
      get: () => props.modelValue,
      set: status => emit('update:modelValue', status)
    })

    /**
     * Handle backdrop click
     * emit close event
     */
    function handleBackdropClick() {
      computedModelValue.value = false
      emit('close')
    }

    /**
     * find bundle price from bundlePreviewData
     */

    const USD = i18n().global.t('pages.bundle.draft.bundle_price.country_codes.USD')

    const bundlePrice = computed(() => props.bundlePreviewData?.prices?.find(price => isEqual(price.currency, USD)))

    /**
     * Feature lazy loading of bundle fonts starts
     */

    const selectedFamiliesWithFonts = computed(() => computedModelValue.value && previewFamilies())

    const chunkSize = 10
    const currentIndex = ref(0)

    const chunkedData = computed(
      () => selectedFamiliesWithFonts.value?.slice(0, currentIndex.value)
    )

    const loadMoreSelectedFonts = () => {
      currentIndex.value += chunkSize
    }

    const previewDraftModalContainer = ref(null)
    const inspirationGalleryRef = ref(null)

    watchEffect(() => {
      if (previewDraftModalContainer.value) {
        previewDraftModalContainer.value.addEventListener('scroll', e => {
          const scrollableHeight =
            previewDraftModalContainer.value.scrollHeight -
            previewDraftModalContainer.value.clientHeight -
            Math.abs(inspirationGalleryRef.value.scrollHeight - 100)

          if (previewDraftModalContainer.value.scrollTop >= scrollableHeight) {
            loadMoreSelectedFonts()
          }
        })
      }
    })

    onMounted(() => {
      loadMoreSelectedFonts()
    })

    watchEffect(() => {
      if(computedModelValue.value) {
        // add is-unscrollable class in body 
        document.querySelector('body').classList.add('is-unscrollable')
      } else {
        document.querySelector('body').classList.remove('is-unscrollable')
      }
    })

    function previewFamilies() {
      const groupedData = groupBy(props.bundlePreviewData.collection, 'familyId')

      return map(groupedData, (collectionFamilies, familyId) => {
        const familyData = head(collectionFamilies)
        familyData.md5 = familyData.styles[0].md5Checksum
        familyData.styleCount = groupedData[familyId].length
        return familyData
      })
    }

    /**
     * Feature lazy loading of bundle fonts ends
     */

    function getFontRenderSrc({ md5, name, size = 20, color = '00000' }) {
      if (typeof md5 !== 'string') return null
      return md5.length
        ? Render.get({
          md5,
          name: encodeURI(name),
          size,
          fg: color
        })
        : null
    }

    /* draft slider image */
    const draftTyperFieldImage = './../../../../../assets/images/draft-type.png'
    const draftPropertiesImage = './../../../../../assets/images/draft-properties.png'
    const draftTyperFieldImageTablet = './../../../../../assets/images/draft-type-tablet.png'
    const draftPropertiesImageTablet = './../../../../../assets/images/draft-properties-tablet.png'
    const draftTyperFieldImageMobile = './../../../../../assets/images/draft-type-mobile.png'
    const draftPropertiesImageMobile = './../../../../../assets/images/draft-properties-mobile.png'

    /**
     * show draft is in preview state
     */

    const previewMessage = ref({
      show: false,
      message: i18n().global.t('pages.bundle.draft.bundle_preview.preview_mode_message')
    })

    function showPreviewModeIsOn() {
      previewMessage.value.show = true
      // automatically hide view only message after 2 seconds
      setTimeout(() => {
        previewMessage.value.show = false
      }, 2000)
    }

    /**
     * draft main image sliders
     */

    const carouselConfigs = ref({
      currentSlide: 0,
      itemsToScroll: 3,
      itemsToShow: 2.5,
      breakpoints: {
        364: {
          itemsToShow: 1,
        },
        719: {
          itemsToShow: 1.1,
        },
        991: {
          itemsToShow: 1.5,
        },
        1279: {
          itemsToShow: 2.5,
        },
      }
    })

    const mainImageCarousel = ref(null)

    watchEffect(() => {
      if (mainImageCarousel.value) {
        mainImageCarousel.value.restartCarousel()
      }
    })

    watch(computedModelValue, newValue => {
      if (newValue) {
        carouselConfigs.value.currentSlide = 0
      }
    })

    const showNextButton = computed(() => {
      const data = mainImageCarousel.value?.data
      return Number(data?.currentSlide.value) + carouselConfigs.value.itemsToShow < props.bundlePreviewData.assets.main.length
    })

    const slideToPrev = () => {
      mainImageCarousel.value.prev()
    }

    const slideToNext = () => {
      mainImageCarousel.value.next()
    }
    /**
     * Show the comma seperated foundries name in the tooltip
     * @param {Number} foundries
     */
    const showBundleFoundries = foundries => {
      return foundries.map(foundry => foundry?.label ?? foundry.name).join(', ')
    }

    return {
      slideToNext,
      slideToPrev,
      closeHandler,
      computedModelValue,
      handleBackdropClick,
      isEmpty,
      bundlePrice,
      selectedFamiliesWithFonts,
      getFontRenderSrc,
      draftTyperFieldImage,
      draftPropertiesImage,
      showPreviewModeIsOn,
      previewMessage,
      carouselConfigs,
      mainImageCarousel,
      showBundleFoundries,
      previewDraftModalContainer,
      chunkedData,
      chunkSize,
      currentIndex,
      inspirationGalleryRef,
      showNextButton,
      draftTyperFieldImageTablet,
      draftPropertiesImageTablet,
      draftTyperFieldImageMobile,
      draftPropertiesImageMobile,
      Misc,
      PREVIEW_DRAFT_CONSTANTS
    }
  }
}
